import Constants from 'expo-constants';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Image, Platform, Pressable, SectionList, View, useWindowDimensions, ScrollView } from 'react-native';
import * as Location from 'expo-location';
import { SvgUri } from 'react-native-svg';
import AuthGuard from '../components/common/AuthGuard';
import { useAppDispatch, useAppSelector } from '../hooks/store-hook';
import LoggedInRestrictedArea from '../components/common/LoggedInRestrictedArea';
import { LoggedState } from '../models/AuthModelsCommon';
import useTranslation from '../hooks/translation-hook';
import useStyles from '../hooks/styles-hook';
import Label, { LabelType } from '../components/common/Label';
import {
  DeviceTheme,
  GeolocationRequirement,
  GetMyMissionDetailsDto,
  GetMyMissionListDto,
  GetMyMissionsListStatusDto,
  MissionAssignationStatus,
  MissionConfigurationDetailType,
  MysteryShopperVerificationStatus,
  MysteryShopperClient,
  MyMissionClient,
  MissionGeolocationCheckInRequest,
  MissionGeolocationCheckInStatus,
  PrevalidationSteps,
  RelatedMinorRelationship,
} from '../api/api.g';
import { useFocusEffect, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import useApi from '../hooks/api-hook';
import { MapLocation } from '../models/MapModels';
import Loading from '../components/common/Loading';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import {
  faAward,
  faBasketShopping,
  faBullseye,
  faCalendar,
  faCalendarClock,
  faChild,
  faChevronDown,
  faCheckDouble,
  faChevronUp,
  faChevronLeft,
  faCircleCheck,
  faCircleXmark,
  faEye,
  faLocation,
  faLocationCheck,
  faMapLocationDot,
  faPenNib,
  faReceipt,
  faStore,
  faTimer,
  faTriangleExclamation,
  faUserSecret,
} from '@fortawesome/pro-regular-svg-icons';
import pin from '../../assets/pin-selected.png';
import CustomMarkdown from '../components/common/CustomMarkDown';
import useCustomTheme from '../hooks/theme-hook';
import NavigationButton from '../components/common/NavigationButton';
import { faCircleChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { lightBoxActions } from '../store/lightBoxSlice';
import Prevalidation from '../components/validation/pages/Prevalidation';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../components/mymission/MyMissionItem';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { missionsActions } from '../store/missionsSlice';
import CustomButton from '../components/common/CustomButton';
import ProfileModal from '../components/myaccount/myprofile/ProfileModal';
import MoreInfoButton from '../components/common/MoreInfoButton';
import { StylesHelper } from '../models/helpers/stylesHelper';
import { StringHelper } from '../helpers/stringHelper';
import { AnalyticsHelper } from '../helpers/analyticsHelper';
import { navBarActions } from '../store/navBarSlice';
import OnARollPopup from '../components/common/mission/MissionOnARollPopup';
import { getForegroundLocationWithoutPrompt } from '../helpers/locationPermission';
import MissionAirportLocationInfo from '../components/common/mission/MissionAirportLocationInfo';
import GeolocationCheckInResultModal from '../components/common/mission/GeolocationCheckInResultModal';
import {
  filterMissionBlocksByStatus,
  MyMissionsStatusFilter,
  type MyMissionsStatusFilter as MyMissionsStatusFilterValue,
} from '../models/helpers/myMissionsFilter';

export enum NavigationState {
  List,
  Details,
}

export enum MissionDetailsState {
  Mission,
  Scenario,
  Conditions,
  Admissibility,
}

type MissionDetailAccordionSection = 'admissibility' | 'conditions';

interface CachedMyMissionsUiState {
  selectedMissionId?: string;
  navigationState?: NavigationState;
  detailsState?: MissionDetailsState;
  missionStatusFilter?: MyMissionsStatusFilterValue;
}

let cachedMyMissionsUiState: CachedMyMissionsUiState = {};

const MISSION_STATUSES_BY_FILTER: Record<Exclude<MyMissionsStatusFilterValue, 'all'>, readonly MissionAssignationStatus[]> = {
  [MyMissionsStatusFilter.Ongoing]: [
    MissionAssignationStatus.Assigned,
    MissionAssignationStatus.ValidationRejected,
    MissionAssignationStatus.PrevalidationFailed,
  ],
  [MyMissionsStatusFilter.InValidation]: [
    MissionAssignationStatus.Prevalidation,
    MissionAssignationStatus.Validating,
    MissionAssignationStatus.FraudDetected,
    MissionAssignationStatus.InappropriateLanguage,
    MissionAssignationStatus.LLMApproved,
    MissionAssignationStatus.LLMRejected,
  ],
  [MyMissionsStatusFilter.Approved]: [MissionAssignationStatus.Approved],
};

const MissionListLogo = ({ logoUrl, defaultLogo, imageStyle }: { logoUrl?: string; defaultLogo: React.ReactNode; imageStyle: any }) => {
  const [hasLoadError, setHasLoadError] = useState(false);

  useEffect(() => {
    setHasLoadError(false);
  }, [logoUrl]);

  const normalizedLogoUrl = logoUrl?.trim();
  const isSvgLogo = !!normalizedLogoUrl && (normalizedLogoUrl.toLowerCase().includes('.svg') || normalizedLogoUrl.toLowerCase().startsWith('data:image/svg'));

  if (!normalizedLogoUrl || hasLoadError) {
    return <>{defaultLogo}</>;
  }

  if (isSvgLogo) {
    // Native SVG logo rendering is intermittently crashing in My Missions; use the default icon there for now.
    if (Platform.OS !== 'web') {
      return <>{defaultLogo}</>;
    }

    return <SvgUri uri={normalizedLogoUrl} width="100%" height="100%" onError={() => setHasLoadError(true)} />;
  }

  return <Image style={imageStyle} source={{ uri: normalizedLogoUrl }} resizeMode="contain" onError={() => setHasLoadError(true)} />;
};

const MyMissions = () => {
  const loggedState = useAppSelector((selector) => selector.auth.logged);
  const authToken = useAppSelector((selector) => selector.auth.idToken);
  const missionListSeed = useAppSelector((selector) => selector.missions.missionListSeed);
  const recentlyAcceptedMission = useAppSelector((selector) => selector.missions.recentlyAcceptedMission);
  const showOnARollPopupRequested = useAppSelector((selector) => selector.missions.showOnARollPopup);
  const userTheme = useAppSelector((selector) => selector.userPrefs.theme);
  const uiLanguage = useAppSelector((selector) => selector.userPrefs.uiLanguage);
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const tMission = useTranslation({ defaultPath: 'mission' });
  const layout = useWindowDimensions();
  const styles = useStyles((section) => section.myMissions);
  const stylesDetail = useStyles((section) => section.missionDetailCard);
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute();
  const routedMissionId = route?.params?.['id'];
  const theme = useCustomTheme();
  const insets = useSafeAreaInsets();
  const isDarkMode = userTheme === DeviceTheme.Dark;
  const postponeInlineColor = isDarkMode ? 'rgb(210, 237, 253)' : 'rgb(52, 114, 184)';
  const surveyUnavailableMessageStyle = {
    color: theme.text.titleLight,
    fontSize: 12,
    textAlign: 'center' as const,
    maxWidth: 320,
    flexShrink: 1,
  };

  const stylesCard = useStyles((section) => section.missionCard);
  const useSinglePaneLayout = Platform.OS !== 'web' || layout.width <= 768;
  const mobileDetailsScrollPaddingBottom = useSinglePaneLayout ? Math.max(insets.bottom, 0) + (Platform.OS === 'ios' ? 88 : 96) : 16;
  const isScreenFocused = useIsFocused();
  const queryClient = useQueryClient();

  const apiMyMission = useApi(MyMissionClient);
  const apiMysteryShopper = useApi(MysteryShopperClient);

  const [userLocation, setUserLocation] = useState<MapLocation>(undefined);
  const defaultPosition: MapLocation = Constants.expoConfig.extra.defautlLocation;

  const initialSelectedMissionId = cachedMyMissionsUiState.selectedMissionId ?? '';
  const [missionBlocks, setMissionBlocks] = useState<GetMyMissionsListStatusDto[] | undefined>(undefined);
  const [selectedMissionId, setSelectedMissionId] = useState(initialSelectedMissionId);
  const [missionStatusFilter, setMissionStatusFilter] = useState<MyMissionsStatusFilterValue>(
    cachedMyMissionsUiState.missionStatusFilter ?? MyMissionsStatusFilter.All
  );
  const [isMissionStatusFilterOpen, setIsMissionStatusFilterOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailsState, setDetailsState] = useState<MissionDetailsState>(
    cachedMyMissionsUiState.detailsState === MissionDetailsState.Scenario ? MissionDetailsState.Scenario : MissionDetailsState.Mission
  );
  const [expandedMissionDetailAccordion, setExpandedMissionDetailAccordion] = useState<MissionDetailAccordionSection | undefined>(undefined);
  const [navigationState, setNavigationState] = useState<NavigationState>(cachedMyMissionsUiState.navigationState);
  const [detail, setDetail] = useState<GetMyMissionDetailsDto | undefined>(undefined);
  const [postponeLoading, setPostponeLoading] = useState<boolean>(false);
  const [extendMissionFailed, setExtendMissionFailed] = useState<boolean>(false);
  const [showOnARollPopup, setShowOnARollPopup] = useState<boolean>(false);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const lastRejectedValidationSeedRefreshRef = useRef<number>(0);

  const formatSurveyUnavailableDate = (date?: Date) => {
    if (!date) {
      return undefined;
    }

    if (uiLanguage?.startsWith('fr')) {
      const day = date.getDate();
      const month = new Intl.DateTimeFormat('fr-CA', { month: 'long' }).format(date);
      const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);

      return `${day === 1 ? '1er' : day} ${capitalizedMonth}`;
    }

    return new Intl.DateTimeFormat(uiLanguage || undefined, { day: 'numeric', month: 'long' }).format(date);
  };

  const formatLocalizedAmount = (value?: number | string) => {
    const numericValue = typeof value === 'number' ? value : Number(String(value ?? '0').replace(',', '.'));

    if (!Number.isFinite(numericValue)) {
      return '0';
    }

    const locale = uiLanguage?.startsWith('fr') ? 'fr-CA' : uiLanguage || undefined;

    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
    }).format(numericValue);
  };

  const getSurveyUnavailableMessage = () => {
    if (detail?.geolocationRequirement === GeolocationRequirement.Required && !detail?.hasGeolocationCheckIn) {
      return Platform.OS === 'web' ? t('geolocation-check-in-native-app-required') : t('geolocation-check-in-required-before-survey');
    }

    if (isCheckingSelectedRelatedMinor) {
      return t('loading');
    }

    if (selectedRelatedMinorIsDeleted) {
      return t('selected-related-minor-deleted-survey-unavailable-message');
    }

    if (selectedRelatedMinorIsNotApproved) {
      return t('selected-related-minor-not-approved-survey-unavailable-message');
    }

    const formattedDate = formatSurveyUnavailableDate(detail?.periodStartDate);

    if (!formattedDate) {
      return t('survey-unavailable-not-yet-message');
    }

    return t('survey-unavailable-not-yet-message', { date: formattedDate, periodStartDate: formattedDate });
  };

  const defaultLogo = useMemo(() => <FontAwesomeIcon icon={faStore} style={stylesCard.header.logo.icon} />, [stylesCard]);
  const selectedMission = useMemo<GetMyMissionListDto | undefined>(() => {
    if (!selectedMissionId || !missionBlocks?.length) {
      return undefined;
    }

    return missionBlocks.flatMap((missionBlock) => missionBlock.missionsList ?? []).find((mission) => mission.id === selectedMissionId);
  }, [missionBlocks, selectedMissionId]);
  const filteredMissionBlocks = useMemo(() => {
    const includedStatuses =
      missionStatusFilter === MyMissionsStatusFilter.All ? undefined : MISSION_STATUSES_BY_FILTER[missionStatusFilter];

    return filterMissionBlocksByStatus(missionBlocks ?? [], includedStatuses);
  }, [missionBlocks, missionStatusFilter]);
  const missionSections = useMemo(
    () =>
      filteredMissionBlocks
        .filter((block) => (block.missionsList?.length ?? 0) > 0)
        .map((block, blockIndex) => ({
          key: `missionBlock_${blockIndex}_${block.status ?? ''}`,
          title: t(MissionAssignationStatus[block.status]),
          data: block.missionsList ?? [],
        })),
    [filteredMissionBlocks, t]
  );
  const missionStatusFilterOptions = useMemo(
    () => [
      { value: MyMissionsStatusFilter.All, label: t('all') },
      { value: MyMissionsStatusFilter.Ongoing, label: t('ongoing_message') },
      { value: MyMissionsStatusFilter.InValidation, label: t('in_validation') },
      { value: MyMissionsStatusFilter.Approved, label: t('Approved') },
    ],
    [t]
  );
  const selectedMissionStatusFilterOption =
    missionStatusFilterOptions.find((option) => option.value === missionStatusFilter) ?? missionStatusFilterOptions[0];

  const relatedMinorRelationshipLabel = (relationship?: RelatedMinorRelationship) => {
    switch (relationship) {
      case RelatedMinorRelationship.Child:
        return t('related-minor-relationship-child');
      case RelatedMinorRelationship.Sibling:
        return t('related-minor-relationship-sibling');
      case RelatedMinorRelationship.LegalGuardian:
        return t('related-minor-relationship-legal-guardian');
      case RelatedMinorRelationship.OtherRelatedFamily:
        return t('related-minor-relationship-other-related-family');
      default:
        return undefined;
    }
  };

  const [navigationEnd, setNavigationEnd] = useState(false);
  const [shouldShowAnonymousPopup, setShouldShowAnonymousPopup] = useState(false);
  const [refreshMap, setRefreshMap] = useState(false);

  useFocusEffect(
    React.useCallback(() => {
      if (loggedState !== undefined && loggedState !== LoggedState.NotLogged) {
        // Always clear map/onboarding lightbox popups when entering My Missions (web + mobile).
        dispatch(lightBoxActions.setVisibility(false));
        dispatch(lightBoxActions.setSecondVisibility(false));
        dispatch(lightBoxActions.setContent(<></>));
        dispatch(lightBoxActions.setSecondContent(<></>));
        dispatch(lightBoxActions.setOverlayVisible(true));
        dispatch(lightBoxActions.setAllowBackgroundInteraction(false));
        dispatch(lightBoxActions.setContentVerticalAlign('center'));
        dispatch(lightBoxActions.setContentHorizontalAlign('center'));
        dispatch(lightBoxActions.setContentTopOffset(0));
        dispatch(lightBoxActions.setContentBottomOffset(0));
        dispatch(lightBoxActions.setContentLeftOffset(0));

        //console.log(`%cNavigation end - ${route.name}`, 'color: #ffa600;');

        AnalyticsHelper.logScreenView(route.name).then(() => {
          //console.log('Analytics', 'Page view: My missions');
        });

        if (showOnARollPopupRequested) {
          dispatch(missionsActions.setShowOnARollPopup(false));
          setShowOnARollPopup(true);
        }

        if (routedMissionId) {
          setMissionStatusFilter(MyMissionsStatusFilter.All);
          setSelectedMissionId(routedMissionId);
        }

        setRefreshMap(true);
        setNavigationState((current) => (current === undefined ? NavigationState.List : current));
        setNavigationEnd(true);
        dispatch(navBarActions.setVisibility(true));
      } else {
        setNavigationEnd(false);
      }
      return () => {
        //console.log(`%cOnBlur - ${route.name}`, 'color: #ffa600;');
        setNavigationEnd(false);
        queryClient.cancelQueries({ queryKey: ['myMissions', 'list'] });
        queryClient.cancelQueries({ queryKey: ['myMissionsDetail'] });
      };
    }, [dispatch, loggedState, queryClient, route.name, routedMissionId, showOnARollPopupRequested])
  );

  useEffect(() => {
    if (recentlyAcceptedMission) {
      setMissionStatusFilter(MyMissionsStatusFilter.All);
      setSelectedMissionId(recentlyAcceptedMission);
    }
  }, [recentlyAcceptedMission]);

  useEffect(() => {
    setExpandedMissionDetailAccordion(undefined);
  }, [selectedMissionId]);

  useEffect(() => {
    let mounted = true;
    getForegroundLocationWithoutPrompt(defaultPosition).then((location) => {
      if (mounted) {
        setUserLocation(location);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  const missionDetailTextByType = useMemo(() => {
    const detailTextByType: Partial<Record<MissionConfigurationDetailType, string>> = {};

    for (const missionDetailItem of detail?.missionDetails ?? []) {
      detailTextByType[missionDetailItem.type] = missionDetailItem.text ?? '';
    }

    return detailTextByType;
  }, [detail?.missionDetails]);

  const objectiveText = missionDetailTextByType[MissionConfigurationDetailType.Objective] ?? '';
  const noteText = missionDetailTextByType[MissionConfigurationDetailType.ToNote] ?? '';
  const niceToKnowText = missionDetailTextByType[MissionConfigurationDetailType.NiceToKnow] ?? '';
  const scenarioText = missionDetailTextByType[MissionConfigurationDetailType.Scenario] ?? '';
  const eligibilityText = missionDetailTextByType[MissionConfigurationDetailType.Eligibility] ?? '';
  const termsAndConditionsText = missionDetailTextByType[MissionConfigurationDetailType.TermsAndConditions] ?? '';

  const toggleMissionDetailAccordion = React.useCallback((section: MissionDetailAccordionSection) => {
    setExpandedMissionDetailAccordion((currentSection) => (currentSection === section ? undefined : section));
  }, []);

  const renderMissionDetailAccordion = React.useCallback(
    (section: MissionDetailAccordionSection, title: string, icon: IconProp, content: string) => {
      const isExpanded = expandedMissionDetailAccordion === section;

      return (
        <View style={styles.newVersion.details.content.details.infoBlocks.accordion.base}>
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ expanded: isExpanded }}
            style={styles.newVersion.details.content.details.infoBlocks.accordion.header}
            onPress={() => toggleMissionDetailAccordion(section)}
          >
            <FontAwesomeIcon icon={icon} style={styles.newVersion.details.content.details.infoBlocks.titleIcon} />
            <Label variant={LabelType.titleDark} style={styles.newVersion.details.content.details.infoBlocks.accordion.title} numberOfLines={3}>
              {title}
            </Label>
            <FontAwesomeIcon
              icon={isExpanded ? faChevronUp : faChevronDown}
              size={16}
              style={styles.newVersion.details.content.details.infoBlocks.accordion.chevron}
            />
          </Pressable>
          {isExpanded && (
            <View style={styles.newVersion.details.content.details.infoBlocks.accordion.content}>
              <CustomMarkdown>{content}</CustomMarkdown>
            </View>
          )}
        </View>
      );
    },
    [expandedMissionDetailAccordion, styles, toggleMissionDetailAccordion]
  );

  const missionDetailContent = useMemo(
    () => (
      <>
        {objectiveText !== '' && (
          <View style={styles.newVersion.details.content.details.infoBlocksBase}>
            <View style={styles.newVersion.details.content.details.infoBlocks.title}>
              <FontAwesomeIcon icon={faBullseye} style={styles.newVersion.details.content.details.infoBlocks.titleIcon} />
              <Label variant={LabelType.titleDark}>{t('Your-objective')}</Label>
              <MoreInfoButton style={{ marginLeft: 8 }} title={''}>
                {`${t('help.your-objective')}`}
              </MoreInfoButton>
            </View>
            <View style={styles.newVersion.details.content.details.infoBlocks.content}>
              <CustomMarkdown>{objectiveText}</CustomMarkdown>
            </View>
          </View>
        )}
        {noteText !== '' && (
          <View style={styles.newVersion.details.content.details.infoBlocksBase}>
            <View style={styles.newVersion.details.content.details.infoBlocks.title}>
              <FontAwesomeIcon icon={faPenNib} style={styles.newVersion.details.content.details.infoBlocks.titleIcon} />
              <Label variant={LabelType.titleDark}>{t('Please-note')}</Label>
              <MoreInfoButton style={{ marginLeft: 8 }} title={''}>
                {`${t('help.please-note')}`}
              </MoreInfoButton>
            </View>
            <View style={styles.newVersion.details.content.details.infoBlocks.content}>
              <CustomMarkdown>{noteText}</CustomMarkdown>
            </View>
          </View>
        )}
        {niceToKnowText !== '' && (
          <View style={styles.newVersion.details.content.details.infoBlocksBase}>
            <View style={styles.newVersion.details.content.details.infoBlocks.title}>
              <FontAwesomeIcon icon={faTriangleExclamation} style={styles.newVersion.details.content.details.infoBlocks.titleIcon} />
              <Label variant={LabelType.titleDark}>{t('Pay-attention')}</Label>
              <MoreInfoButton style={{ marginLeft: 8 }} title={''}>
                {`${t('help.nice-to-know')}`}
              </MoreInfoButton>
            </View>
            <View style={styles.newVersion.details.content.details.infoBlocks.content}>
              <CustomMarkdown>{niceToKnowText}</CustomMarkdown>
            </View>
          </View>
        )}
        {scenarioText !== '' && (
          <View style={styles.newVersion.details.content.details.infoBlocksBase}>
            <View style={styles.newVersion.details.content.details.infoBlocks.title}>
              <FontAwesomeIcon icon={faUserSecret} style={styles.newVersion.details.content.details.infoBlocks.titleIcon} />
              <Label variant={LabelType.titleDark}>{t('The-scenario')}</Label>
              <MoreInfoButton style={{ marginLeft: 8 }} title={''} openURL={'the-scenario-lanla-website-support-link'}>
                {t('help.the-scenario')}
              </MoreInfoButton>
            </View>
            <View style={styles.newVersion.details.content.details.infoBlocks.content}>
              <CustomMarkdown>{scenarioText}</CustomMarkdown>
              <Pressable style={styles.newVersion.details.content.details.infoBlocks.link} onPress={() => setDetailsState(MissionDetailsState.Scenario)}>
                <Label variant={LabelType.caption}>{t('Read-more')}</Label>
              </Pressable>
            </View>
          </View>
        )}
        {eligibilityText !== '' && renderMissionDetailAccordion('admissibility', t('Admissibility'), faCheckDouble, eligibilityText)}
        {termsAndConditionsText !== '' && renderMissionDetailAccordion('conditions', t('Terms-and-Conditions'), faReceipt, termsAndConditionsText)}
      </>
    ),
    [eligibilityText, niceToKnowText, noteText, objectiveText, renderMissionDetailAccordion, scenarioText, styles, t, termsAndConditionsText]
  );

  const missionScenarioContent = useMemo(
    () => (
      <>
        <Label variant={LabelType.title} style={styles.newVersion.details.header.subTitle}>
          {t('scenario')}
        </Label>
        <CustomMarkdown>{scenarioText}</CustomMarkdown>
        <NavigationButton
          style={[styles.newVersion.details.content.details.infoBlocks.baseLinks, { marginTop: 20 }]}
          icon={faChevronLeft}
          iconSize={16}
          color={theme.text.titleLight}
          labelVariant={LabelType.titleSmall}
          onPress={() => setDetailsState(MissionDetailsState.Mission)}
          text={t('back')}
        />
      </>
    ),
    [scenarioText, styles, t, theme.text.titleLight]
  );

  const detailsComponent = useMemo(() => {
    switch (detailsState) {
      case MissionDetailsState.Scenario:
        return missionScenarioContent;
      case MissionDetailsState.Mission:
      default:
        return missionDetailContent;
    }
  }, [detailsState, missionDetailContent, missionScenarioContent]);

  useEffect(() => {
    if (!navigationState && useSinglePaneLayout) {
      setNavigationState(NavigationState.List);
    }
  }, [navigationState, useSinglePaneLayout]);

  useEffect(() => {
    setShouldShowAnonymousPopup(loggedState === LoggedState.Anonymous);
  }, [loggedState]);

  useEffect(() => {
    cachedMyMissionsUiState = {
      ...cachedMyMissionsUiState,
      selectedMissionId,
      navigationState,
      detailsState,
      missionStatusFilter,
    };
  }, [selectedMissionId, navigationState, detailsState, missionStatusFilter]);

  useEffect(() => {
    if (!selectedMissionId) {
      setDetail(undefined);
      setLoadingDetail(false);
      return;
    }

    const cachedQueryData = queryClient.getQueryData(['myMissionsDetail', selectedMissionId, missionListSeed]) as any;
    const cachedDetail = cachedQueryData?.missionDetails as GetMyMissionDetailsDto | undefined;

    if (cachedDetail) {
      setDetail(cachedDetail);
      setLoadingDetail(false);
      return;
    }

    setDetail(undefined);
    setLoadingDetail(!!navigationEnd && isScreenFocused && !!authToken && loggedState > 1);
  }, [authToken, isScreenFocused, loggedState, missionListSeed, navigationEnd, queryClient, selectedMissionId]);

  const canLoadMyMissions = !!navigationEnd && isScreenFocused && !!authToken && !!missionListSeed;
  const canLoadMissionDetails = !!navigationEnd && isScreenFocused && !!selectedMissionId && !!authToken && loggedState > 1;

  const myMissionsQuery = useQuery({
    enabled: canLoadMyMissions,
    staleTime: 30_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    queryKey: ['myMissions', 'list', authToken, missionListSeed],
    queryFn: ({ signal }) => {
      if (!missionBlocks || missionBlocks.length === 0) {
        setLoading(true);
      }

      const promise = apiMyMission.getMissionsList(apiMyMission.tokenSource?.token);

      // Cancel the request if React Query signals to abort
      signal?.addEventListener('abort', () => {
        setLoading(false);

        apiMyMission.tokenSource?.cancel('Query was cancelled by React Query');
        apiMyMission.onCancel();
      });

      return promise.finally(() => {
        setLoading(false);
      });
    },
  });

  const hasCachedMissionBlocks = !!missionBlocks && missionBlocks.length > 0;
  const isWaitingForMissionListBootstrap =
    !!navigationEnd &&
    loggedState !== LoggedState.NotLogged &&
    !hasCachedMissionBlocks &&
    (!authToken || !missionListSeed || loading || !myMissionsQuery.isFetched || myMissionsQuery.isLoading || myMissionsQuery.isFetching);
  const shouldShowEmptyMissionState =
    !isWaitingForMissionListBootstrap &&
    !loading &&
    myMissionsQuery.isFetched &&
    !myMissionsQuery.isLoading &&
    !myMissionsQuery.isFetching &&
    (!missionBlocks || missionBlocks.length === 0);

  const missionDetailQuery = useQuery({
    enabled: canLoadMissionDetails,
    retry: false,
    useErrorBoundary: false,
    staleTime: 30_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    queryKey: ['myMissionsDetail', selectedMissionId, missionListSeed],
    queryFn: ({ signal }) => {
      // Cancel the request if React Query signals to abort
      signal?.addEventListener('abort', () => {
        apiMyMission.tokenSource?.cancel('Query was cancelled by React Query');
        apiMyMission.onCancel();
      });

      const cachedQueryData = queryClient.getQueryData(['myMissionsDetail', selectedMissionId, missionListSeed]) as any;

      if (!cachedQueryData?.missionDetails) {
        setLoadingDetail(true);
      }

      return apiMyMission.getMissionDetails(selectedMissionId, apiMyMission.tokenSource?.token);
    },
    // useErrorBoundary: (error: any) => {
    //   if (error.status === 404) {
    //     dispatch(lightBoxActions.setContent(<Label>Error</Label>));
    //     dispatch(lightBoxActions.setVisibility(true));
    //     console.log(' test');
    //   }
    // },
  });

  const selectedRelatedMinorId = detail?.selectedRelatedMinor?.id;
  const selectedRelatedMinorVerificationQuery = useQuery({
    enabled: canLoadMissionDetails && !!selectedRelatedMinorId,
    retry: false,
    useErrorBoundary: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    queryKey: ['myMissions', 'selectedRelatedMinorVerification', authToken, selectedRelatedMinorId],
    queryFn: ({ signal }) => {
      signal?.addEventListener('abort', () => {
        apiMysteryShopper.tokenSource?.cancel('Query was cancelled by React Query');
        apiMysteryShopper.onCancel();
      });

      return apiMysteryShopper.getIdVerification(apiMysteryShopper.tokenSource?.token);
    },
  });
  const selectedRelatedMinorVerification = (selectedRelatedMinorVerificationQuery.data?.relatedMinors ?? []).find((minor) => minor.id === selectedRelatedMinorId);
  const selectedRelatedMinorIsDeleted =
    !!selectedRelatedMinorId &&
    selectedRelatedMinorVerificationQuery.isSuccess &&
    !selectedRelatedMinorVerification;
  const selectedRelatedMinorIsNotApproved =
    !!selectedRelatedMinorId &&
    selectedRelatedMinorVerificationQuery.isSuccess &&
    !!selectedRelatedMinorVerification &&
    selectedRelatedMinorVerification.status !== MysteryShopperVerificationStatus.Approved;
  const isCheckingSelectedRelatedMinor =
    !!selectedRelatedMinorId && (selectedRelatedMinorVerificationQuery.isLoading || selectedRelatedMinorVerificationQuery.isFetching);
  const canAnswerCurrentMissionSurvey =
    detail?.canAnswerSurvey !== false && !selectedRelatedMinorIsDeleted && !selectedRelatedMinorIsNotApproved && !isCheckingSelectedRelatedMinor;

  const showCheckInMessage = (message: string, allowSettings = false) => {
    const handleClose = () => {
      dispatch(lightBoxActions.setNoDismiss(false));
      dispatch(lightBoxActions.setVisibility(false));
      dispatch(lightBoxActions.setContent(<></>));
    };

    dispatch(lightBoxActions.setNoDismiss(true));
    dispatch(
      lightBoxActions.setContent(
        <GeolocationCheckInResultModal message={message} allowSettings={allowSettings} onClose={handleClose} />
      )
    );
    dispatch(lightBoxActions.setIsLoading(false));
    dispatch(lightBoxActions.setVisibility(true));
  };

  const handleCheckIn = async () => {
    if (Platform.OS === 'web' || !selectedMissionId || checkInLoading) {
      return;
    }

    setCheckInLoading(true);
    try {
      const permission = await Location.requestForegroundPermissionsAsync();
      if (permission.status !== Location.PermissionStatus.GRANTED) {
        showCheckInMessage(t('geolocation-permission-denied'), true);
        return;
      }

      if (!(await Location.hasServicesEnabledAsync())) {
        showCheckInMessage(t('geolocation-services-disabled'), true);
        return;
      }

      const position = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Highest });
      const request = new MissionGeolocationCheckInRequest();
      request.latitude = position.coords.latitude;
      request.longitude = position.coords.longitude;
      request.accuracyMeters = position.coords.accuracy ?? Number.MAX_SAFE_INTEGER;

      const result = await apiMyMission.checkIn(selectedMissionId, request, apiMyMission.tokenSource?.token);
      switch (result.status) {
        case MissionGeolocationCheckInStatus.Success:
        case MissionGeolocationCheckInStatus.AlreadyCheckedIn:
          await missionDetailQuery.refetch();
          showCheckInMessage(t('geolocation-check-in-success'));
          break;
        case MissionGeolocationCheckInStatus.OutsideRadius:
          showCheckInMessage(t('geolocation-outside-radius', { distance: Math.round(result.distanceMeters ?? 0), radius: result.allowedRadiusMeters }));
          break;
        case MissionGeolocationCheckInStatus.InsufficientAccuracy:
          showCheckInMessage(t('geolocation-insufficient-accuracy', { accuracy: result.maximumAccuracyMeters }));
          break;
        case MissionGeolocationCheckInStatus.MissingBranchCoordinates:
          showCheckInMessage(t('geolocation-missing-store-coordinates'));
          break;
        case MissionGeolocationCheckInStatus.MissionUnavailable:
          showCheckInMessage(t('geolocation-mission-unavailable'));
          break;
        default:
          showCheckInMessage(t('geolocation-check-in-failed'));
          break;
      }
    } catch {
      showCheckInMessage(t('geolocation-timeout-or-unavailable'), true);
    } finally {
      setCheckInLoading(false);
    }
  };

  useEffect(() => {
    if (!isScreenFocused || !myMissionsQuery.isSuccess || myMissionsQuery.dataUpdatedAt === 0) {
      return;
    }

    if (lastRejectedValidationSeedRefreshRef.current === myMissionsQuery.dataUpdatedAt) {
      return;
    }

    lastRejectedValidationSeedRefreshRef.current = myMissionsQuery.dataUpdatedAt;
    dispatch(missionsActions.setReloadRejectedValidationSeed(StringHelper.generateRandomString()));
  }, [dispatch, isScreenFocused, myMissionsQuery.dataUpdatedAt, myMissionsQuery.isSuccess]);

  useEffect(() => {
    if (myMissionsQuery.isSuccess) {
      if (myMissionsQuery.data) {
        const value = myMissionsQuery.data;
        const missionsListArray = myMissionsQuery.data.missions.reduce((accumulator, currentMission) => {
          // Check if missionsList array is defined and not empty
          if (currentMission.missionsList && currentMission.missionsList.length > 0) {
            // Concatenate the missionsList array to the accumulator
            return accumulator.concat(currentMission.missionsList);
          } else {
            return accumulator;
          }
        }, []);

        setMissionBlocks(value.missions);

        if (missionsListArray?.length > 0) {
          if (selectedMissionId && missionsListArray.findIndex((m) => m.id === selectedMissionId) > -1) {
            dispatch(missionsActions.setMissionAcceptedId(undefined));

            if (useSinglePaneLayout) {
              setNavigationState(NavigationState.Details);
            }
          }
        } else {
          setMissionBlocks(undefined);
        }

        if (!selectedMissionId) {
          setLoading(false);
        }
      }
    }

    if (myMissionsQuery.isError) {
      setLoading(false);
    }
  }, [myMissionsQuery.isSuccess, myMissionsQuery.isError, myMissionsQuery.data, selectedMissionId, useSinglePaneLayout]);

  useEffect(() => {
    if (missionDetailQuery.isSuccess) {
      const missionDetail = (missionDetailQuery.data as any).missionDetails;
      setDetail(missionDetail);

      setLoadingDetail(false);
      setLoading(false);
    }

    if (missionDetailQuery.isError) {
      setLoadingDetail(false);
    }
  }, [missionDetailQuery.isSuccess, missionDetailQuery.isError, missionDetailQuery.data]);

  useEffect(() => {
    if (useSinglePaneLayout || !missionBlocks) {
      return;
    }

    const visibleMissions = missionSections.flatMap((section) => section.data);
    const selectedMissionIsVisible = visibleMissions.some((mission) => mission.id === selectedMissionId);

    if ((!selectedMissionId && !routedMissionId) || (selectedMissionId && !selectedMissionIsVisible)) {
      setSelectedMissionId(visibleMissions[0]?.id);
    }
  }, [missionBlocks, missionSections, routedMissionId, selectedMissionId, useSinglePaneLayout]);

  const finalizeCancelledMissionFlow = () => {
    setLoading(false);
    setLoadingDetail(false);
    dispatch(lightBoxActions.setNoDismiss(false));
    setDetail(undefined);
    setSelectedMissionId(undefined);
    setNavigationState(NavigationState.List);
    dispatch(missionsActions.setReloadMissionListSeed(String.prototype.generateRandomString(5)));

    dispatch(
      lightBoxActions.setContent(
        <ProfileModal hideConfirm title={t('success')}>
          <View style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <FontAwesomeIcon icon={faCircleCheck} size={48} color={'#43a49e'} />
            <Label style={{ marginTop: 8, textAlign: 'center' }}>{t('mission.cancel_success')}</Label>
          </View>
        </ProfileModal>
      )
    );
    dispatch(lightBoxActions.setVisibility(true));
  };

  const cancelMissionMutation = useMutation({
    retry: false,
    mutationFn: async (missionId: string) => {
      try {
        const result = await apiMyMission.cancelMyMission(missionId, apiMyMission.tokenSource?.token);
        return { ok: true as const, result };
      } catch (error: any) {
        return { ok: false as const, error };
      }
    },
    onSuccess(payload) {
      if (payload.ok) {
        finalizeCancelledMissionFlow();
        return;
      }

      const error = payload.error;
      const errorStatus = error?.status ?? error?.response?.status;
      const errorMessage = `${error?.message ?? ''} ${error?.response?.data?.title ?? ''} ${error?.response?.data?.detail ?? ''}`.toLowerCase();
      const shouldFallbackToCancelled =
        [404, 409, 410, 500].includes(errorStatus) ||
        errorMessage.includes('mission unavailable') ||
        errorMessage.includes('not available') ||
        errorMessage.includes('unavailable') ||
        errorMessage.includes('unexpected server error');

      if (shouldFallbackToCancelled) {
        finalizeCancelledMissionFlow();
        return;
      }

      finalizeCancelledMissionFlow();
    },
  });

  const delayMissionMutation = useMutation({
    mutationFn: (params: { missionId: string; requestDay: number }) => {
      setExtendMissionFailed(false);
      setPostponeLoading(true);
      return apiMyMission.extendMyMission(params.missionId, params.requestDay, apiMyMission.tokenSource?.token);
    },
    onSuccess(data, variables) {
      setPostponeLoading(false);
      dispatch(missionsActions.setReloadMissionListSeed(String.prototype.generateRandomString(5)));

      dispatch(
        lightBoxActions.setContent(
          <ProfileModal hideConfirm title={t('mission-extended')}>
            <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
              <FontAwesomeIcon icon={faCalendarClock} size={24} color={theme.text.titleDark.toString()} />
              <Label style={{ marginLeft: 8 }} variant={LabelType.body1}>
                {t('mission-delay-confirm', { count: variables.requestDay })}
              </Label>
            </View>
          </ProfileModal>
        )
      );

      dispatch(lightBoxActions.setVisibility(true));
    },
    onError() {
      setPostponeLoading(false);
      setExtendMissionFailed(true);
    },
  });

  const showPrompt = () => {
    if (!detail || !canAnswerCurrentMissionSurvey) {
      return;
    }

    if (detail.status === MissionAssignationStatus.ValidationRejected || detail.inProgress) {
      navigation.navigate(detail.status === MissionAssignationStatus.ValidationRejected ? 'editSurvey' : 'survey', { missionAssignationId: selectedMissionId });
    } else {
      dispatch(
        lightBoxActions.setContent(
          <Prevalidation
            refreshMap={refreshMap}
            onMapRefreshed={() => {
              setRefreshMap(false);
            }}
            phone={selectedMission?.phone}
            selectedMissionId={selectedMissionId}
            onClosePress={() => {
              dispatch(lightBoxActions.setVisibility(false));
              dispatch(lightBoxActions.setContent(<></>));
            }}
            onConfirmPress={() => {
              dispatch(lightBoxActions.setVisibility(false));
              dispatch(lightBoxActions.setContent(<></>));

              navigation.navigate(detail.status === MissionAssignationStatus.ValidationRejected ? 'editSurvey' : 'survey', {
                missionAssignationId: selectedMissionId,
              });
            }}
          />
        )
      );
      dispatch(lightBoxActions.setVisibility(true));
    }
  };

  const getTimeLineItem = (title: string, icon: IconProp, date?: Date) => {
    const isDarkTheme = isDarkMode;
    const timelineActiveIconColor = isDarkTheme ? (theme.timelineIcon?.toString() ?? '#40a098') : (theme.timelineIcon?.toString() ?? '#ffffff');
    const timelineDisabledIconColor = isDarkTheme ? (theme.text.darkTurquoiseTwo?.toString() ?? '#5FC8BA') : theme.text.darkTurquoiseTwo.toString();

    let status = detail.status;
    let iconBaseStyle = {};
    let dottedLineStyle = {};
    let contentStyle = {};
    let itemStyle = {};
    let iconColor = timelineActiveIconColor;
    let hasDottedLine = true;
    let fillSurvey = false;
    let validationFailed = false;
    let supportText = '';
    let supportLink = undefined;
    let isTimelineErrorStep = false;
    const isGeolocationTimelineItem = title === 'geolocation-check-in-title';
    const isRequiredCheckInPending =
      detail.geolocationRequirement === GeolocationRequirement.Required && !detail.hasGeolocationCheckIn;

    if (title === 'Mission-accepted') {
      dottedLineStyle = styles.newVersion.details.content.timeline.dottedBottom;
      hasDottedLine = true;
      supportText = `${t('help.mission-deadline')}`;
      supportLink = 'mission-deadline-lanla-website-support-link';
    }

    if (title === 'Mission-accomplished') {
      supportText = `${t('help.mission-accomplished')}`;

      if (status === MissionAssignationStatus.Assigned) {
        dottedLineStyle = styles.newVersion.details.content.timeline.dottedUp;
        hasDottedLine = true;
        fillSurvey = true;
        supportText = `${t('help.complete-the-questionnaire')}`;
        supportLink = 'complete-the-questionnaire-lanla-website-support-link';

        if (isRequiredCheckInPending) {
          contentStyle = styles.newVersion.details.content.timeline.contentDisabled;
          iconBaseStyle = styles.newVersion.details.content.timeline.icon.disabled;
          iconColor = timelineDisabledIconColor;
        } else {
          iconBaseStyle = styles.newVersion.details.content.timeline.icon.active;
          itemStyle = styles.newVersion.details.content.timeline.contentActive;
          iconColor = timelineActiveIconColor;
        }
      }

      if (status >= MissionAssignationStatus.Prevalidation) {
        dottedLineStyle = styles.newVersion.details.content.timeline.dottedBoth;
        hasDottedLine = true;
      }
    }

    if (isGeolocationTimelineItem) {
      dottedLineStyle = styles.newVersion.details.content.timeline.dottedBoth;
      hasDottedLine = true;
      supportText = detail.hasGeolocationCheckIn
        ? t('geolocation-check-in-completed')
        : detail.geolocationRequirement === GeolocationRequirement.Required
          ? t('geolocation-check-in-required-notice', { radius: detail.geolocationRadiusMeters })
          : t('geolocation-check-in-optional-notice', { radius: detail.geolocationRadiusMeters });

      if (detail.hasGeolocationCheckIn) {
        icon = faCircleCheck;
        iconBaseStyle = styles.newVersion.details.content.timeline.icon.active;
        iconColor = timelineActiveIconColor;
      } else if (detail.geolocationRequirement === GeolocationRequirement.Required) {
        iconBaseStyle = styles.newVersion.details.content.timeline.icon.active;
        itemStyle = styles.newVersion.details.content.timeline.contentActive;
        iconColor = timelineActiveIconColor;
      } else {
        contentStyle = styles.newVersion.details.content.timeline.contentDisabled;
        iconBaseStyle = styles.newVersion.details.content.timeline.icon.disabled;
        iconColor = timelineDisabledIconColor;
      }
    }

    if (title === 'Mission-in-prevalidation') {
      supportText = `${t('help.mission-in-prevalidation')}`;
      supportLink = 'mission-under-validation-lanla-website-support-link';
      if (status === MissionAssignationStatus.Prevalidation) {
        iconBaseStyle = styles.newVersion.details.content.timeline.icon.active;
        itemStyle = styles.newVersion.details.content.timeline.contentActive;
        dottedLineStyle = styles.newVersion.details.content.timeline.dottedUp;
        iconColor = timelineActiveIconColor;
        hasDottedLine = true;
      }

      if (status >= MissionAssignationStatus.Validating) {
        dottedLineStyle = styles.newVersion.details.content.timeline.dottedBoth;
        hasDottedLine = true;
      }
      if (status < MissionAssignationStatus.Prevalidation) {
        contentStyle = styles.newVersion.details.content.timeline.contentDisabled;
        iconBaseStyle = styles.newVersion.details.content.timeline.icon.disabled;
        iconColor = timelineDisabledIconColor;
        hasDottedLine = false;
      }
      if (status === MissionAssignationStatus.PrevalidationFailed) {
        dottedLineStyle = styles.newVersion.details.content.timeline.dottedUp;
        hasDottedLine = true;
        icon = faCircleXmark;
        itemStyle = styles.newVersion.details.content.timeline.contentError;
        iconBaseStyle = styles.newVersion.details.content.timeline.icon.disabled;
        iconColor = theme.text.error.toString();
        isTimelineErrorStep = true;
      }
    }

    if (title === 'Mission-in-validation') {
      supportText = `${t('help.mission-under-validation')}`;
      supportLink = 'mission-under-validation-lanla-website-support-link';
      if (status === MissionAssignationStatus.Validating) {
        iconBaseStyle = styles.newVersion.details.content.timeline.icon.active;
        itemStyle = styles.newVersion.details.content.timeline.contentActive;
        dottedLineStyle = styles.newVersion.details.content.timeline.dottedUp;
        iconColor = timelineActiveIconColor;
        hasDottedLine = true;
      }

      if (status === MissionAssignationStatus.Approved) {
        dottedLineStyle = styles.newVersion.details.content.timeline.dottedBoth;
        hasDottedLine = true;
      }

      if (status < MissionAssignationStatus.Validating || status === MissionAssignationStatus.PrevalidationFailed) {
        contentStyle = styles.newVersion.details.content.timeline.contentDisabled;
        iconBaseStyle = styles.newVersion.details.content.timeline.icon.disabled;
        iconColor = timelineDisabledIconColor;
        hasDottedLine = false;
      }

      if (status === MissionAssignationStatus.ValidationRejected) {
        dottedLineStyle = styles.newVersion.details.content.timeline.dottedUp;
        hasDottedLine = true;
        icon = faCircleXmark;
        itemStyle = styles.newVersion.details.content.timeline.contentError;
        iconBaseStyle = styles.newVersion.details.content.timeline.icon.disabled;
        iconColor = theme.text.error.toString();
        validationFailed = true;
        supportText = `${t('help.mission-under-validation-correct-the-questionnaire')}`;
        isTimelineErrorStep = true;
      }
    }

    if (title === 'Mission-approved') {
      if (status === MissionAssignationStatus.Approved) {
        iconBaseStyle = styles.newVersion.details.content.timeline.icon.active;
        itemStyle = styles.newVersion.details.content.timeline.contentActive;
        dottedLineStyle = styles.newVersion.details.content.timeline.dottedUp;
        iconColor = timelineActiveIconColor;
        hasDottedLine = true;
      } else {
        contentStyle = styles.newVersion.details.content.timeline.contentDisabled;
        iconBaseStyle = styles.newVersion.details.content.timeline.icon.disabled;
        iconColor = timelineDisabledIconColor;
        hasDottedLine = false;
      }
      supportText = `${t('help.approved-mission')}`;
      // supportLink = `mission-approved-lanla-website-support`;
    }

    const useDarkErrorStyles = isDarkMode && isTimelineErrorStep;
    const errorTimelineStyles = styles.newVersion.details.content.timeline;
    const titleLabelStyle = [contentStyle, useDarkErrorStyles && errorTimelineStyles.contentErrorTitle];
    const secondaryLabelStyle = useDarkErrorStyles ? errorTimelineStyles.contentErrorSecondary : undefined;
    const promptTitleLabelStyle = [contentStyle, useDarkErrorStyles && errorTimelineStyles.contentErrorTitle, { fontWeight: '400', flexShrink: 1 }];
    const promptDateLabelStyle = [contentStyle, useDarkErrorStyles && errorTimelineStyles.contentErrorTitle, { fontWeight: '500', flexShrink: 1 }];
    const isSurveyUnavailable = !canAnswerCurrentMissionSurvey;
    const actionLabelStyle = {
      fontWeight: '600' as const,
      textDecorationLine: 'underline' as const,
      ...(useDarkErrorStyles ? errorTimelineStyles.contentErrorAction : {}),
    };
    const disabledActionLabelStyle = isSurveyUnavailable ? { color: theme.text.titleLight, textDecorationLine: 'none' as const } : undefined;
    const moreInfoColor = useDarkErrorStyles ? (errorTimelineStyles.contentErrorInfo.color as string | undefined) : undefined;

    return (
      <View style={[styles.newVersion.details.content.timeline.item, itemStyle]}>
        {hasDottedLine && <View style={[styles.newVersion.details.content.timeline.dottedLine, dottedLineStyle]}></View>}
        <View style={[styles.newVersion.details.content.timeline.iconBase, iconBaseStyle]}>
          <FontAwesomeIcon icon={icon} size={Platform.OS === 'web' ? 20 : 18} color={iconColor} />
        </View>
        <View style={styles.newVersion.details.content.timeline.content}>
          {fillSurvey || validationFailed ? (
            <>
              {detail.daysLeft !== null && (
                <>
                  <Label variant={LabelType.titleSmall} style={promptTitleLabelStyle} numberOfLines={2}>
                    {validationFailed ? t('Validation-failed') : t('Mission-expires-the')}
                  </Label>
                  {!!detail.endDate && (
                    <Label variant={LabelType.titleSmall} style={promptDateLabelStyle} numberOfLines={2}>
                      {detail.endDate.toLocaleString()}
                    </Label>
                  )}
                </>
              )}
              <Pressable disabled={isSurveyUnavailable} onPress={showPrompt}>
                <Label variant={LabelType.body1} style={[actionLabelStyle, disabledActionLabelStyle]}>
                  {validationFailed ? t('Correct-my-questionnaire') : t('Complete-the-questionnaire')}
                </Label>
              </Pressable>
              {isSurveyUnavailable && Platform.OS !== 'web' && (
                <Label style={[surveyUnavailableMessageStyle, { textAlign: 'left' }]} numberOfLines={3}>
                  {getSurveyUnavailableMessage()}
                </Label>
              )}
            </>
          ) : (
            <>
              <Label variant={LabelType.titleSmall} style={titleLabelStyle}>
                {t(title)}
              </Label>
              {isGeolocationTimelineItem && (
                <Label variant={LabelType.body1} style={secondaryLabelStyle}>
                  {detail.hasGeolocationCheckIn
                    ? t('geolocation-check-in-completed')
                    : detail.geolocationRequirement === GeolocationRequirement.Required
                      ? t('Pending')
                      : t('Optional')}
                </Label>
              )}
              {date && (
                <Label variant={LabelType.body1} style={secondaryLabelStyle}>
                  {date.toLocaleDateString()}
                </Label>
              )}
            </>
          )}
        </View>
        <MoreInfoButton style={{ marginLeft: 8 }} title={''} color={moreInfoColor} openURL={supportLink}>
          {supportText}
        </MoreInfoButton>
      </View>
    );
  };

  const handleDelayMissionClick = (missionId: string, maxDays: number) => {
    delayMissionMutation.mutate({ missionId, requestDay: maxDays });
  };

  const renderCancelMissionPrompt = (missionId: string, isLoading: boolean) => {
    return (
      <ProfileModal
        loading={isLoading}
        hideCloseButton={isLoading}
        onClose={() => {
          if (isLoading) {
            return;
          }

          dispatch(lightBoxActions.setVisibility(false));
          dispatch(lightBoxActions.setContent(<></>));
        }}
        onConfirm={() => {
          if (!missionId || cancelMissionMutation.isPending || isLoading) {
            return;
          }

          dispatch(lightBoxActions.setNoDismiss(true));
          dispatch(lightBoxActions.setContent(renderCancelMissionPrompt(missionId, true)));
          cancelMissionMutation.mutate(missionId);
        }}
      >
        <View style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            size={48}
            color={isDarkMode ? (theme.text.error?.toString() ?? '#ff9bb4') : theme.colorErrorPrimary.toString()}
          />
          <Label style={{ marginTop: 8 }}>{t('are-you-sure-you-want-to-cancel-the-mission')} </Label>
          <Label
            variant={LabelType.titlePrimarySmall}
            style={{ marginTop: 8, textAlign: 'center', color: isDarkMode ? (theme.text.darkTurquoiseTwo?.toString() ?? '#5FC8BA') : undefined }}
          >
            {t('it-will-be-returned-online')}
          </Label>
        </View>
      </ProfileModal>
    );
  };

  const showConfirmationPrompt = () => {
    if (!selectedMissionId) {
      return;
    }

    dispatch(lightBoxActions.setNoDismiss(false));
    dispatch(lightBoxActions.setContent(renderCancelMissionPrompt(selectedMissionId, false)));
    dispatch(lightBoxActions.setVisibility(true));
  };

  const prevalidationCardBackgroundColor = isDarkMode ? theme.background.missionDetail.toString() : 'rgb(235, 244, 243)';
  const prevalidationBorderColor = isDarkMode ? theme.borderColorLight.toString() : '#ffffff';
  const prevalidationTitleColor = isDarkMode ? theme.text.primary.toString() : '#035164';
  const prevalidationItemTitleColor = isDarkMode ? theme.text.primary.toString() : '#035164';
  const prevalidationPendingColor = theme.primary.toString();
  const prevalidationErrorColor = isDarkMode ? '#ff8aa4' : '#8e183d';
  const prevalidationErrorHeadlineColor = isDarkMode ? (theme.text.primary?.toString() ?? '#ffffff') : '#f05869';
  const prevalidationErrorTitleColor = isDarkMode ? '#ffd2dc' : '#8e183d';
  const prevalidationApprovedColor = isDarkMode ? theme.text.darkTurquoiseTwo.toString() : '#035164';
  const prevalidationFailedCardBackgroundColor = isDarkMode ? 'rgba(142, 24, 61, 0.18)' : '#feeef0';
  const getPrevalidationIcon = (enumFlag: PrevalidationSteps, prevalidationSteps: PrevalidationSteps) => {
    const isStepActive = (enumFlag: PrevalidationSteps, step: PrevalidationSteps) => {
      return (enumFlag & step) === step;
    };

    if (detail.status === MissionAssignationStatus.Prevalidation) {
      return <FontAwesomeIcon icon={faTimer} size={24} color={prevalidationPendingColor} />;
    } else if (isStepActive(enumFlag, prevalidationSteps)) {
      return <FontAwesomeIcon icon={faCircleXmark} size={24} color={prevalidationErrorColor} />;
    } else {
      return <FontAwesomeIcon icon={faCircleCheck} size={24} color={prevalidationApprovedColor} />;
    }
  };

  const getPrevalidationText = (enumFlag: PrevalidationSteps, prevalidationSteps: PrevalidationSteps, customText: string) => {
    const isStepActive = (enumFlag: PrevalidationSteps, step: PrevalidationSteps) => {
      return (enumFlag & step) === step;
    };
    if (detail.status === MissionAssignationStatus.Prevalidation) {
      return <Label style={{ color: prevalidationPendingColor, fontSize: 16, flexWrap: 'wrap' }}>{t('Pending')}</Label>;
    } else if (isStepActive(enumFlag, prevalidationSteps)) {
      return (
        <Label numberOfLines={20} style={{ color: prevalidationErrorColor, fontSize: 16, flexWrap: 'wrap' }}>
          {customText}
        </Label>
      );
    } else {
      return <Label style={{ color: prevalidationApprovedColor, fontSize: 16, flexWrap: 'wrap' }}>{t('Approved')}</Label>;
    }
  };

  const getEmptyComponent = () => {
    return (
      <View
        style={{
          height: '100%',
          flexGrow: 1,
          flexShrink: 1,
          flexDirection: 'column',
          alignContent: 'center',
          justifyContent: 'center',
          ...StylesHelper.padding(16, 24),
        }}
      >
        <Label variant={LabelType.titleLargeNoCapsPrimary} style={styles.newVersion.list.title}>
          {t('my_missions')}
        </Label>
        <ScrollView style={{ flexGrow: 1, flexShrink: 1, marginTop: 16 }}>
          <View style={{ alignItems: 'center', flexShrink: 0 }}>
            <Image style={{ width: 229, height: 274, alignSelf: 'center' }} source={require('../../assets/NoValidations.png')} />
            {/* <Label variant={LabelType.titleLargeNoCapsPrimary} style={{ textAlign: 'center', fontSize: 30, flexShrink: 1 }}>
              {t('oh')}
            </Label> */}
            <Label variant={LabelType.titleLargeLight} style={{ textAlign: 'center', fontWeight: '700', fontSize: 20, flexShrink: 1, marginTop: 16 }}>
              {tMission('no_missions_assigned')}
            </Label>
            <Label style={{ textAlign: 'center', alignSelf: 'center', fontSize: 14, width: 175, paddingVertical: 24, flexShrink: 1 }} numberOfLines={5}>
              {t('oppotunity-explore-missions')}
            </Label>
            <View style={{ flexDirection: 'row', marginTop: 24 }}>
              <CustomButton
                title={t('missions-available')}
                onPress={() => {
                  navigation.navigate('map' as never);
                }}
              />
            </View>
          </View>
        </ScrollView>
      </View>
    );
  };

  const getDetailComponent = () => {
    return (
      <>
        {detail.status == MissionAssignationStatus.Approved && detailsState !== MissionDetailsState.Scenario ? (
          <>
            <View style={styles.newVersion.details.content.rightContainer}>
              <View
                style={{
                  flexGrow: 1,
                  flexShrink: 1,
                  padding: 16,
                  flexDirection: 'column',
                  alignContent: 'center',
                  justifyContent: 'center',
                }}
              >
                <ScrollView style={{ flexGrow: 1, flexShrink: 1 }}>
                  <View style={{ flexShrink: 0, alignItems: 'center' }}>
                    <View style={{ position: 'relative' }}>
                      <Image style={{ width: 290, height: undefined, aspectRatio: 586 / 544 }} source={require('../../assets/approved.png')} />
                      <Label
                        variant={LabelType.titleLargeNoCapsPrimary}
                        style={{
                          position: 'absolute',
                          top: 63,
                          left: 187,
                          transform: [{ rotate: '-12deg' }],
                          fontSize: 20,
                          color: theme.text.darkTurquoiseTwo,
                          textAlign: 'center',
                          width: 90,
                          fontWeight: 900,
                        }}
                      >
                        {formatLocalizedAmount(detail.totalAmount)}
                      </Label>
                    </View>
                    <Label
                      variant={LabelType.titleLargeNoCapsPrimary}
                      style={{
                        textAlign: 'center',
                        fontSize: 35,
                        color: theme.text.darkTurquoiseTwo,
                        flexShrink: 1,
                        marginTop: 10,
                      }}
                    >
                      {t('Congratulations')}
                    </Label>
                    <Label
                      variant={LabelType.titleLargeNoCapsPrimary}
                      style={{
                        textAlign: 'center',
                        fontSize: 25,
                        color: theme.text.sea,
                        flexShrink: 1,
                      }}
                    >
                      {t('Mission-approved')}
                    </Label>
                    <Label
                      style={{ textAlign: 'center', alignSelf: 'center', fontSize: 16, width: 220, paddingVertical: 18, flexShrink: 0, fontWeight: 500 }}
                      numberOfLines={5}
                    >
                      {t('Earn-even-more-by-completing-new-missions')}
                    </Label>
                    <View style={{ flexDirection: 'row', marginTop: 10 }}>
                      <CustomButton
                        title={t('missions-available')}
                        onPress={() => {
                          navigation.navigate('map' as never);
                        }}
                      />
                    </View>
                  </View>
                </ScrollView>
              </View>
            </View>
          </>
        ) : (detail.status === MissionAssignationStatus.Prevalidation || detail.status === MissionAssignationStatus.PrevalidationFailed) &&
          detailsState !== MissionDetailsState.Scenario ? (
          <View style={{ flexShrink: 1, width: '100%', maxWidth: 900 }}>
            <View style={{ borderRadius: 8, padding: 16, backgroundColor: prevalidationCardBackgroundColor }}>
              <Label variant={LabelType.titleLargeDark} style={{ fontWeight: 700, color: prevalidationTitleColor }}>
                {t('Pre-validation')}
              </Label>
              <View style={{ marginTop: 20 }}>
                <View
                  style={{
                    minHeight: 80,
                    paddingVertical: 16,
                    borderTopWidth: 2,
                    borderTopColor: prevalidationBorderColor,
                    display: 'flex',
                    flexDirection: 'row',
                  }}
                >
                  <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', width: 50 }}>
                    {getPrevalidationIcon(detail.prevalidationSteps, PrevalidationSteps.Bill)}
                  </View>
                  <View style={{ flexShrink: 1, width: '100%' }}>
                    <Label style={{ fontSize: 16, fontWeight: 700, color: prevalidationItemTitleColor }}>{t('Bill')}</Label>
                    {getPrevalidationText(detail.prevalidationSteps, PrevalidationSteps.Bill, t('bill-prevalidation-failed'))}
                  </View>
                </View>
                <View
                  style={{
                    minHeight: 80,
                    paddingVertical: 16,
                    borderTopWidth: 2,
                    borderTopColor: prevalidationBorderColor,
                    display: 'flex',
                    flexDirection: 'row',
                  }}
                >
                  <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', width: 50 }}>
                    {getPrevalidationIcon(detail.prevalidationSteps, PrevalidationSteps.WrongPlace)}
                  </View>
                  <View style={{ flexShrink: 1, width: '100%' }}>
                    <Label style={{ fontSize: 16, fontWeight: 700, color: prevalidationItemTitleColor }}>{t('Place')}</Label>
                    {getPrevalidationText(detail.prevalidationSteps, PrevalidationSteps.WrongPlace, t('place-prevalidation-failed'))}
                  </View>
                </View>
                <View
                  style={{
                    minHeight: 80,
                    paddingVertical: 16,
                    borderTopWidth: 2,
                    borderTopColor: prevalidationBorderColor,
                    display: 'flex',
                    flexDirection: 'row',
                  }}
                >
                  <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', width: 50 }}>
                    {getPrevalidationIcon(detail.prevalidationSteps, PrevalidationSteps.DateAndTime)}
                  </View>
                  <View style={{ flexShrink: 1, width: '100%' }}>
                    <Label style={{ fontSize: 16, fontWeight: 700, color: prevalidationItemTitleColor }}>{t('Date-and-time')}</Label>
                    {getPrevalidationText(detail.prevalidationSteps, PrevalidationSteps.DateAndTime, t('date-and-time-prevalidation-failed'))}
                  </View>
                </View>
                <View
                  style={{
                    minHeight: 80,
                    paddingVertical: 16,
                    borderTopWidth: 2,
                    borderTopColor: prevalidationBorderColor,
                    display: 'flex',
                    flexDirection: 'row',
                  }}
                >
                  <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', width: 50 }}>
                    {getPrevalidationIcon(detail.prevalidationSteps, PrevalidationSteps.FraudDetection)}
                  </View>
                  <View style={{ flexShrink: 1, width: '100%' }}>
                    <Label style={{ fontSize: 16, fontWeight: 700, color: prevalidationItemTitleColor }}>{t('Fraud-detection')}</Label>
                    {getPrevalidationText(detail.prevalidationSteps, PrevalidationSteps.FraudDetection, t('fraud-prevalidation-failed'))}
                  </View>
                </View>
                <View
                  style={{
                    minHeight: 80,
                    paddingVertical: 16,
                    borderTopWidth: 2,
                    borderTopColor: prevalidationBorderColor,
                    display: 'flex',
                    flexDirection: 'row',
                  }}
                >
                  <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', width: 50 }}>
                    {getPrevalidationIcon(detail.prevalidationSteps, PrevalidationSteps.IncorrectProduct)}
                  </View>
                  <View style={{ flexShrink: 1, width: '100%' }}>
                    <Label style={{ fontSize: 16, fontWeight: 700, color: prevalidationItemTitleColor }}>{t('Product')}</Label>
                    {getPrevalidationText(detail.prevalidationSteps, PrevalidationSteps.IncorrectProduct, t('product-prevalidation-failed'))}
                  </View>
                </View>
                <View
                  style={{
                    minHeight: 80,
                    paddingVertical: 16,
                    borderTopWidth: 2,
                    borderTopColor: prevalidationBorderColor,
                    display: 'flex',
                    flexDirection: 'row',
                  }}
                >
                  <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', width: 50 }}>
                    {getPrevalidationIcon(detail.prevalidationSteps, PrevalidationSteps.DifferentMissionTypesSameDay)}
                  </View>
                  <View style={{ flexShrink: 1, width: '100%' }}>
                    <Label style={{ fontSize: 16, fontWeight: 700, color: prevalidationItemTitleColor }}>{t('Mission-type-restriction')}</Label>
                    {getPrevalidationText(detail.prevalidationSteps, PrevalidationSteps.DifferentMissionTypesSameDay, t('mission-type-prevalidation-failed'))}
                  </View>
                </View>
              </View>
            </View>
            {detail.status === MissionAssignationStatus.PrevalidationFailed && (
              <View
                style={{
                  borderRadius: 8,
                  padding: 20,
                  backgroundColor: prevalidationFailedCardBackgroundColor,
                  marginTop: 15,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Image
                  style={{ width: 100, aspectRatio: 188 / 137, tintColor: isDarkMode ? (theme.text.error?.toString() ?? '#ff9bb8') : undefined }}
                  source={require('../../assets/target-prevalidation.png')}
                />
                <Label style={{ color: prevalidationErrorHeadlineColor, fontSize: 28, fontWeight: 700 }}>{t('Oops')}</Label>
                <Label style={{ color: prevalidationErrorTitleColor, fontSize: 24, fontWeight: 700 }}>{t('Pre-validation-failure')}</Label>
                <Label numberOfLines={20} style={{ marginTop: 20, fontSize: 16, flexWrap: 'wrap', textAlign: 'center', paddingHorizontal: 20 }}>
                  {t('prevalidation-failed-general-text')}
                </Label>
                <View style={{ flexDirection: layout.width >= 1060 ? 'row' : 'column', marginTop: 16, gap: 10 }}>
                  <View style={{ flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <CustomButton
                      title={t('Correct-my-questionnaire')}
                      style={{
                        backgroundColor: theme.colorErrorPrimary?.toString() ?? '#8e183d',
                        borderColor: theme.colorErrorPrimary?.toString() ?? '#8e183d',
                        flexGrow: 1,
                      }}
                      textStyle={{ color: '#ffffff' }}
                      disabled={!canAnswerCurrentMissionSurvey}
                      onPress={showPrompt}
                    />
                    {!canAnswerCurrentMissionSurvey && (
                      <Label style={surveyUnavailableMessageStyle} numberOfLines={3}>
                        {getSurveyUnavailableMessage()}
                      </Label>
                    )}
                  </View>
                  <View style={{ flexDirection: 'row' }}>
                    <CustomButton
                      title={t('Cancel-mission')}
                      style={{ backgroundColor: 'transparent', borderColor: isDarkMode ? (theme.text.error?.toString() ?? '#ff9bb8') : '#8e183d', flexGrow: 1 }}
                      textStyle={{ color: isDarkMode ? (theme.text.error?.toString() ?? '#ff9bb8') : '#8e183d' }}
                      onPress={() => {
                        showConfirmationPrompt();
                      }}
                    />
                  </View>
                </View>
              </View>
            )}
          </View>
        ) : (
          <>
            {(detail.status !== MissionAssignationStatus.Validating && detail.status !== MissionAssignationStatus.ValidationRejected) ||
            detailsState === MissionDetailsState.Scenario ? (
              <View style={styles.newVersion.details.content.rightContainer}>
                <View style={styles.newVersion.details.content.details.container}>
                  <View style={styles.newVersion.details.content.details.container}>{detailsComponent}</View>
                </View>
              </View>
            ) : (
              <View style={styles.newVersion.details.content.rightContainer}>
                <View
                  style={{
                    flexGrow: 1,
                    flexShrink: 1,
                    padding: 16,
                    flexDirection: 'column',
                    alignContent: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ScrollView style={{ flexGrow: 1, flexShrink: 1 }}>
                    <View style={{ flexShrink: 0, alignItems: 'center' }}>
                      <Image style={{ width: 229, height: 274 }} source={require('../../assets/InValidations.png')} />
                      <Label
                        variant={LabelType.titleLargeNoCapsPrimary}
                        style={{
                          textAlign: 'center',
                          fontSize: 30,
                          color: detail.status === MissionAssignationStatus.ValidationRejected ? theme.text.error : theme.text.sea,
                        }}
                      >
                        {detail.status === MissionAssignationStatus.ValidationRejected ? t('Validation-failed') : t('Mission-in-validation')}
                      </Label>
                      {detail.status !== MissionAssignationStatus.ValidationRejected ? (
                        <>
                          <Label style={{ textAlign: 'center', alignSelf: 'center', fontSize: 14, width: 175, paddingVertical: 24 }}>
                            {t('oppotunity-explore-missions')}
                          </Label>

                          <View style={{ flexDirection: 'row', marginTop: 24 }}>
                            <CustomButton
                              title={t('missions-available')}
                              onPress={() => {
                                navigation.navigate('map' as never);
                              }}
                            />
                          </View>
                        </>
                      ) : (
                        <View style={{ alignItems: 'center', justifyContent: 'center' }}>
                          <Label numberOfLines={5} style={{ flexShrink: 1, marginTop: 24, textAlign: 'center' }}>
                            {t('Unfortunately-your-recent-mystery-shopping-survey')}
                          </Label>
                          <View style={{ flexDirection: 'row', marginTop: 24 }}>
                            <CustomButton
                              title={t('Correct-my-questionnaire')}
                              disabled={!canAnswerCurrentMissionSurvey}
                              onPress={() => {
                                showPrompt();
                              }}
                            />
                          </View>
                          {!canAnswerCurrentMissionSurvey && (
                            <Label style={[surveyUnavailableMessageStyle, { marginTop: 8 }]} numberOfLines={3}>
                              {getSurveyUnavailableMessage()}
                            </Label>
                          )}
                        </View>
                      )}
                    </View>
                  </ScrollView>
                </View>
              </View>
            )}
          </>
        )}
      </>
    );
  };

  const getMissionExpirationExt = (daysLeft?: number) => {};

  const getTimeline = (margin: boolean) => (
    <>
      <View style={[{ ...styles.newVersion.details.content.timeline.container }, { marginTop: margin ? 16 : 0 }]}>
        {getTimeLineItem('Mission-accepted', faLocation, detail.createdDate)}
        {detail.geolocationRequirement !== GeolocationRequirement.No &&
          getTimeLineItem('geolocation-check-in-title', faMapLocationDot, detail.geolocationCheckIn?.checkedInAtUtc)}
        {getTimeLineItem('Mission-accomplished', faLocationCheck, detail.submittedDate)}
        {getTimeLineItem('Mission-in-prevalidation', faEye)}
        {getTimeLineItem('Mission-in-validation', faCheckDouble)}
        {getTimeLineItem('Mission-approved', faAward, detail.validatedDate)}
      </View>
      <View style={styles.newVersion.details.content.summary.container}>
        {false && detail.tags != undefined && detail.tags != '' && (
          <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItem}>
            <FontAwesomeIcon
              icon={faStore}
              color={theme.text.primary.toString()}
              size={20}
              style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemIcon}
            />
            <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, fontWeight: '700' }}>
              {detail.tags}
            </Label>
          </View>
        )}
        <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItem}>
          <FontAwesomeIcon
            icon={faAward}
            color={theme.text.primary.toString()}
            style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemIcon}
            size={20}
          />
          <Label variant={LabelType.valueLargeMission} style={{ marginLeft: 6, minWidth: 50, flexShrink: 0 }}>
            {uiLanguage === 'en' ? `$${formatLocalizedAmount(detail.salaryAmount)}` : `${formatLocalizedAmount(detail.salaryAmount)} $`}
          </Label>
          <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemValueSecondary}>
            <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, fontWeight: '700' }}>
              {t('salary')}
            </Label>
            <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, fontWeight: '700' }}>
              ({detail.salaryPoints ?? 0} pts)
            </Label>
          </View>
          <MoreInfoButton style={{ alignSelf: 'flex-start', marginLeft: 8 }} title={''}>
            {`${t('help.points-rate')}`}
          </MoreInfoButton>
        </View>
        {detail.requiredPurchase && (
          <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItem}>
            <FontAwesomeIcon
              icon={faBasketShopping}
              color={theme.text.primary.toString()}
              style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemIcon}
              size={20}
            />
            <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, fontWeight: '700' }}>
              {t('purchase_required')}
            </Label>
          </View>
        )}
        {detail.reimbursementValue != undefined && detail.reimbursementValue != '' && (
          <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItem}>
            <FontAwesomeIcon
              icon={faReceipt}
              color={theme.text.primary.toString()}
              style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemIcon}
              size={20}
            />
            <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemValue}>
              <Label variant={LabelType.valueLargeMission} style={{ marginLeft: 6, minWidth: 50, flexShrink: 0 }}>{`${detail.reimbursementValue}`}</Label>
              <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemValueSecondary}>
                <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, flexShrink: 1, fontWeight: '700' }}>
                  {detail.status === MissionAssignationStatus.Assigned ? `${t('reimbursement-up-to')} ${detail.reimbursementValue}` : t('reimbursement')}
                </Label>
                {detail.status === MissionAssignationStatus.Assigned && detail.reimbursementMaxAmount > 0 && (
                  <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, flexShrink: 1, fontWeight: '700' }}>
                    Max {detail.reimbursementMaxAmount} $ ({detail.reimbursementMaxPoints} pts)
                  </Label>
                )}
              </View>
            </View>
            <MoreInfoButton style={{ alignSelf: 'flex-start', marginLeft: 8 }} title={''}>
              {`${t('help.points-rate')}`}
            </MoreInfoButton>
          </View>
        )}
        {detail.time != undefined && (
          <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItem}>
            <FontAwesomeIcon
              icon={faCalendar}
              color={theme.text.primary.toString()}
              style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemIcon}
              size={20}
            />
            <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, fontWeight: '700' }} numberOfLines={10}>
              {detail.time}
            </Label>
          </View>
        )}
        {detail.selectedRelatedMinor &&
          (() => {
            const minor = detail.selectedRelatedMinor;
            const name = `${minor.firstName ?? ''} ${minor.lastName ?? ''}`.trim();
            const relationship = relatedMinorRelationshipLabel(minor.relationship);

            return (
              <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItem}>
                <FontAwesomeIcon
                  icon={faChild}
                  color={theme.text.primary.toString()}
                  style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemIcon}
                  size={20}
                />
                <View style={stylesDetail.missionsBranchDetail.content.infoContainer.summaryItemValueSecondary}>
                  <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, fontWeight: '700' }}>
                    {t('selected-related-minor')}
                  </Label>
                  <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, flexShrink: 1, fontWeight: '700' }} numberOfLines={2}>
                    {name}
                  </Label>
                  {!!relationship && (
                    <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, flexShrink: 1, fontWeight: '700' }} numberOfLines={2}>
                      {`(${relationship})`}
                    </Label>
                  )}
                </View>
              </View>
            );
          })()}
      </View>
      <View style={styles.newVersion.details.content.actionButtonsContainer}>
        {postponeLoading ? (
          <View style={{ height: 40, marginBottom: 16 }}>
            <Loading />
          </View>
        ) : (
          <View style={{ display: 'flex', gap: 16 }}>
            {extendMissionFailed && (
              <Label style={{ color: theme.text.error, fontWeight: '600', flexShrink: 0 }} numberOfLines={5}>
                {t('extend-mission-fail')}
              </Label>
            )}
            {detail.isExtendable && (
              <Pressable
                style={styles.newVersion.details.content.delayMission.container}
                onPress={() => handleDelayMissionClick(selectedMissionId, detail.maxExtendableDays)}
              >
                <FontAwesomeIcon icon={faCalendarClock} size={Platform.OS === 'web' ? 32 : 24} color={'rgb(52, 114, 184)'} />
                <Label variant={LabelType.title} style={styles.newVersion.details.content.delayMission.text} numberOfLines={3}>
                  {t('Postpone-mission')}
                </Label>
              </Pressable>
            )}
          </View>
        )}
        {!useSinglePaneLayout && (detail.status < MissionAssignationStatus.Prevalidation || detail.status == MissionAssignationStatus.ValidationRejected) && (
          <Pressable
            style={[styles.newVersion.details.content.cancelMission.container, { marginBottom: 16 }]}
            onPress={() => {
              showConfirmationPrompt();
            }}
          >
            <FontAwesomeIcon icon={faCircleXmark} size={Platform.OS === 'web' ? 32 : 24} color={'rgb(138, 47, 72)'} />
            <Label variant={LabelType.title} style={styles.newVersion.details.content.cancelMission.text}>
              {t('Cancel-mission')}
            </Label>
            <MoreInfoButton style={{ marginLeft: 8 }} title={''} color={'rgb(138, 47, 72)'} openURL={'cancel-the-mission-lanla-website-support-link'}>
              {`${t('help.cancel-the-mission')}`}
            </MoreInfoButton>
          </Pressable>
        )}

        <Pressable
          style={[
            styles.newVersion.details.content.cancelMission.container,
            { backgroundColor: isDarkMode ? (theme.background.secondary?.toString() ?? '#344450') : '#e9f5f3' },
          ]}
          onPress={() =>
            detailsState === MissionDetailsState.Scenario ? setDetailsState(MissionDetailsState.Mission) : setDetailsState(MissionDetailsState.Scenario)
          }
        >
          <FontAwesomeIcon
            icon={faUserSecret}
            size={Platform.OS === 'web' ? 32 : 24}
            color={isDarkMode ? (theme.text.darkTurquoiseTwo?.toString() ?? '#5FC8BA') : '#43a49e'}
          />
          <Label
            variant={LabelType.title}
            style={[
              styles.newVersion.details.content.cancelMission.text,
              {
                color: isDarkMode ? (theme.text.darkTurquoiseTwo?.toString() ?? '#5FC8BA') : '#43a49e',
                textDecorationColor: isDarkMode ? (theme.text.darkTurquoiseTwo?.toString() ?? '#5FC8BA') : '#43a49e',
              },
            ]}
          >
            {detailsState !== MissionDetailsState.Scenario ? t('Consult-the-scenario') : t('Check-the-status')}
          </Label>
        </Pressable>
      </View>
    </>
  );

  return (
    <AuthGuard>
      {showOnARollPopup && (
        <OnARollPopup
          onClose={() => {
            setShowOnARollPopup(false);
          }}
          onMissionSelect={(missions) => {
            navigation.navigate({
              name: 'map',
              params: { pos: missions.branchId, location: `${missions.geocoordinate.y}|${missions.geocoordinate.x}` },
            } as never);

            setShowOnARollPopup(false);
          }}
        />
      )}
      {shouldShowAnonymousPopup && navigationEnd && <LoggedInRestrictedArea message={t('need-login-before', { action: t('see-your-missions') })} />}
      {!shouldShowAnonymousPopup && navigationEnd && (
        <>
          {useSinglePaneLayout && navigationState === NavigationState.Details && (
            <NavigationButton
              style={styles.newVersion['detailsWrapper.backButton']}
              icon={faCircleChevronLeft}
              iconSize={24}
              labelVariant={LabelType.titleSmall}
              onPress={() => {
                setNavigationState(NavigationState.List);
                setSelectedMissionId(undefined);
              }}
              text={t('back')}
            />
          )}
          <View style={[{ ...styles.containerBase }, { paddingBottom: useSinglePaneLayout ? 32 : 0 }]}>
            {(!useSinglePaneLayout || navigationState === NavigationState.List) && missionBlocks?.length > 0 && (
              <View style={styles.container.sideBar}>
                {!loading ? (
                  <>
                    {missionBlocks && missionBlocks.length > 0 ? (
                      <SectionList
                        sections={missionSections}
                        keyExtractor={(mission) => mission.id}
                        stickySectionHeadersEnabled={false}
                        keyboardShouldPersistTaps="handled"
                        nestedScrollEnabled={true}
                        contentContainerStyle={{ paddingBottom: 16 }}
                        initialNumToRender={12}
                        maxToRenderPerBatch={12}
                        windowSize={8}
                        updateCellsBatchingPeriod={16}
                        removeClippedSubviews={Platform.OS !== 'web'}
                        ListHeaderComponent={
                          <>
                            <View style={{ flexDirection: 'row' }}>
                              <Label variant={LabelType.titleLargeNoCapsPrimary} style={styles.newVersion.list.status}>
                                {t('my_missions')}
                              </Label>
                              <MoreInfoButton title={t('help_support')} openURL={'my-missions-lanla-website-support-link'}>{`${t(
                                'help.when-you-choose-a-mission'
                              )}`}</MoreInfoButton>
                            </View>
                            <View style={styles.newVersion.list.statusFilter.base}>
                              <Pressable
                                accessibilityRole="button"
                                accessibilityLabel={selectedMissionStatusFilterOption.label}
                                accessibilityState={{ expanded: isMissionStatusFilterOpen }}
                                testID="my-missions-status-filter-trigger"
                                onPress={() => setIsMissionStatusFilterOpen((isOpen) => !isOpen)}
                                style={styles.newVersion.list.statusFilter.trigger}
                              >
                                <Label style={styles.newVersion.list.statusFilter.triggerText}>
                                  {selectedMissionStatusFilterOption.label}
                                </Label>
                                <FontAwesomeIcon
                                  icon={isMissionStatusFilterOpen ? faChevronUp : faChevronDown}
                                  color={theme.text.primary.toString()}
                                  size={16}
                                />
                              </Pressable>
                              {isMissionStatusFilterOpen ? (
                                <View testID="my-missions-status-filter-menu" style={styles.newVersion.list.statusFilter.menu}>
                                  {missionStatusFilterOptions.map((option, optionIndex) => {
                                    const isSelected = option.value === missionStatusFilter;

                                    return (
                                      <Pressable
                                        key={option.value}
                                        accessibilityRole="button"
                                        accessibilityState={{ selected: isSelected }}
                                        testID={`my-missions-status-filter-${option.value}`}
                                        onPress={() => {
                                          setMissionStatusFilter(option.value);
                                          setIsMissionStatusFilterOpen(false);
                                        }}
                                        style={[
                                          styles.newVersion.list.statusFilter.option,
                                          optionIndex === missionStatusFilterOptions.length - 1
                                            ? styles.newVersion.list.statusFilter.optionLast
                                            : undefined,
                                          isSelected ? styles.newVersion.list.statusFilter.optionSelected : undefined,
                                        ]}
                                      >
                                        <Label
                                          style={[
                                            styles.newVersion.list.statusFilter.optionText,
                                            isSelected ? styles.newVersion.list.statusFilter.optionTextSelected : undefined,
                                          ]}
                                        >
                                          {option.label}
                                        </Label>
                                        {isSelected ? (
                                          <FontAwesomeIcon icon={faCircleCheck} color={theme.text.inverse.toString()} size={16} />
                                        ) : null}
                                      </Pressable>
                                    );
                                  })}
                                </View>
                              ) : null}
                            </View>
                          </>
                        }
                        ListEmptyComponent={
                          <Label style={styles.newVersion.list.statusFilter.emptyMessage}>{t('no-mission-filter')}</Label>
                        }
                        renderSectionHeader={({ section }) => (
                          <Label variant={LabelType.titlePrimarySmall} style={styles.newVersion.list.status}>
                            {section.title}
                          </Label>
                        )}
                        renderItem={({ item: mission }) => (
                          <Pressable
                            style={[styles.newVersion.list.item, mission.id === selectedMissionId ? styles.newVersion.list.itemSelected : {}]}
                            onPress={() => {
                              setSelectedMissionId(mission.id);

                              if (Platform.OS === 'web') {
                                window.history.pushState({}, '', `/mymissions`);
                              }

                              if (useSinglePaneLayout) {
                                setNavigationState(NavigationState.Details);
                              }
                            }}
                          >
                            <View style={styles.newVersion.list.logoBase}>
                              <MissionListLogo logoUrl={mission.logoUrl} defaultLogo={defaultLogo} imageStyle={styles.newVersion.list.logo.image} />
                            </View>
                            <View style={{ flexShrink: 1 }}>
                              <Label
                                numberOfLines={2}
                                style={{
                                  color: theme.text.primary,
                                  fontSize: 16,
                                  textTransform: 'uppercase',
                                  fontWeight: '600',
                                }}
                              >{`${mission.missionConfigurationName}`}</Label>
                              <Label numberOfLines={2} style={{ color: theme.text.primary, fontSize: 13, fontWeight: '600', marginBottom: 4 }}>
                                {`${mission.name}`}
                              </Label>
                              <Label style={{ color: theme.text.primary, fontSize: 12, fontWeight: '400' }}>{`${mission.address}`}</Label>
                            </View>
                          </Pressable>
                        )}
                      />
                    ) : (
                      <>
                        <View style={{ flexDirection: 'row' }}>
                          <Label variant={LabelType.titleLargeNoCapsPrimary} style={styles.newVersion.list.status}>
                            {t('my_missions')}
                          </Label>
                          <MoreInfoButton title={t('help_support')}>
                            {`# Rudis inferius

## Multis sis velox quercus usus frequens Minervaetransformabantur

Lorem markdownum obligor repetam dignabere qua Sigea crinem Cillan succrescere
tollit meliora quem. Auro ferunt? At Aventino ferunt tamen
[nodosque](http://salutant.net/est), et cognovit rurisque **in illud manibus**
ager, [omnia](http://quamquam.net/) in vulnera. Telum conscendunt accensis
hinnitus gravis claudit. Est tantum Perimelen iuratus Cerberei signum atra moras
finibus volat [navis margine omnes](http://in.net/) Cybeleius.

> Unde **mea onus** Verque, nec obstaret, sint, unguibus ut dente Pithecusas
> nulla, cadunt nec agitur. Reminiscor quantas ab vulnera Oeneus et poterat sub
> media eminet, Phoebe. Hiatu excipiunt quodque [efficiet
> arma](http://www.quo.net/in-dixerunt.html), tridente ut undas nutritur
> Credulitas pectora undarum quae durat cultu motus! Volvitur et populis ducis
> matri reserato **inque**, magnum quantumque, nomenque apparuit. Vitamque et
> iniqua magnanimi et aether tacitorum tibi Aethiopum, eum illi domo gaudete
> tenus miserum.

Oculi iam vidit nisi, artesque ac vicibus reice, altum sentit crinitas prope,
vix. Sed o commentaque, superatae exierat vulgi, quotiensque nais haerentem
quaque cunasque lucibus, memorare se. Satis coniunx: nec sic manu tuum est
duritiem diversa.`}
                          </MoreInfoButton>
                        </View>
                        <View style={{ flexGrow: 1, flexShrink: 1 }}>
                          <Label style={{ textAlign: 'center', width: '100%', margin: 'auto', paddingLeft: 16, paddingRight: 16 }} variant={LabelType.title}>
                            No missions message. Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                          </Label>
                        </View>
                      </>
                    )}
                  </>
                ) : (
                  <View style={{ flexGrow: 1 }}>
                    <Loading style={{ height: 200 }} text={tMission('loading_missions')} />
                  </View>
                )}
              </View>
            )}
            {(!useSinglePaneLayout || navigationState === NavigationState.Details) && missionBlocks?.length > 0 && (
              <>
                {detail && (
                  <View style={styles.newVersion.detailsContainer}>
                    {!loadingDetail ? (
                      <ScrollView style={styles.newVersion.detailsWrapper} contentContainerStyle={{ paddingBottom: mobileDetailsScrollPaddingBottom }}>
                        <View style={styles.newVersion.details.header.container}>
                          <View>
                            <Image style={styles.newVersion.details.header.pin} source={pin} />
                          </View>
                          <View style={styles.newVersion.details.header.title}>
                            <Label
                              numberOfLines={2}
                              style={{
                                color: theme.text.primary,
                                fontSize: 16,
                                textTransform: 'uppercase',
                                fontWeight: '600',
                                flexShrink: 1,
                              }}
                            >
                              {selectedMission?.missionConfigurationName}
                            </Label>
                            <Label numberOfLines={2} style={{ color: theme.text.primary, fontSize: 13, fontWeight: '600', marginBottom: 4, flexShrink: 1 }}>
                              {selectedMission?.name}
                            </Label>
                            <Pressable
                              onPress={() => {
                                dispatch(missionsActions.setSelectedBranch({ id: undefined, lat: undefined, lng: undefined }));
                                navigation.navigate({
                                  name: 'map',
                                  params: { pos: selectedMission.branchId, location: `${selectedMission.geoLocation.y}|${selectedMission.geoLocation.x}` },
                                } as never);
                              }}
                              style={{ flexDirection: 'row', alignItems: 'center' }}
                            >
                              <Label variant={LabelType.titleDarkSmall} style={{ flexShrink: 1 }}>
                                {selectedMission?.address}
                              </Label>
                              <FontAwesomeIcon icon={faMapLocationDot} size={14} style={{ marginLeft: 4 }} color={theme.text.titleDark.toString()} />
                            </Pressable>
                            <MissionAirportLocationInfo
                              branchAirportInfo={detail?.branchAirportInfo}
                              containerStyle={{ marginTop: 4 }}
                              textStyle={{ color: theme.text.primary, fontWeight: '500' }}
                              textVariant={LabelType.titleDarkSmall}
                            />
                            <>
                              {!!selectedMission?.phone && (
                                <Label variant={LabelType.titleDarkSmall} style={{ flexShrink: 1 }}>
                                  {selectedMission?.phone}
                                </Label>
                              )}
                            </>

                            <Label variant={LabelType.caption} style={{ color: theme.text.titleLight, marginTop: 4 }}>
                              {detail?.tags === '' ? 'N/A' : detail?.tags}
                            </Label>
                          </View>
                          <View
                            style={[
                              styles.newVersion.details.header.submit,
                              useSinglePaneLayout && {
                                flexDirection: 'column',
                                flexBasis: '100%',
                                flexGrow: 0,
                                width: '100%',
                                maxWidth: '100%',
                                minWidth: 0,
                                alignItems: 'stretch',
                              },
                            ]}
                          >
                            {detail.geolocationRequirement !== GeolocationRequirement.No &&
                              !detail.hasGeolocationCheckIn &&
                              Platform.OS !== 'web' && (
                              <View style={styles.newVersion.details.header.checkIn.card}>
                                <View style={styles.newVersion.details.header.checkIn.header}>
                                  <View style={styles.newVersion.details.header.checkIn.icon}>
                                    <FontAwesomeIcon icon={faMapLocationDot} size={18} color={theme.background.primary.toString()} />
                                  </View>
                                  <View style={styles.newVersion.details.header.checkIn.copy}>
                                    <Label style={styles.newVersion.details.header.checkIn.title}>{t('geolocation-check-in-title')}</Label>
                                    <Label style={styles.newVersion.details.header.checkIn.description}>
                                      {detail.geolocationRequirement === GeolocationRequirement.Required
                                        ? t('geolocation-check-in-required-notice', { radius: detail.geolocationRadiusMeters })
                                        : t('geolocation-check-in-optional-notice', { radius: detail.geolocationRadiusMeters })}
                                    </Label>
                                  </View>
                                </View>
                                <CustomButton
                                  title={checkInLoading ? t('geolocation-checking-in') : t('geolocation-check-in-now')}
                                  disabled={checkInLoading}
                                  onPress={handleCheckIn}
                                  style={styles.newVersion.details.header.checkIn.button}
                                />
                              </View>
                            )}
                            {Platform.OS !== 'web' && detail.hasGeolocationCheckIn && detail.geolocationRequirement !== GeolocationRequirement.No && (
                              <Label style={{ color: theme.text.primary, textAlign: 'center', fontSize: 12, marginBottom: 8 }}>
                                {t('geolocation-check-in-completed')}
                              </Label>
                            )}
                            {(detail.status === MissionAssignationStatus.Assigned ||
                              detail.status === MissionAssignationStatus.ValidationRejected ||
                              detail.status == MissionAssignationStatus.PrevalidationFailed) && (
                              <View style={{ gap: 8, alignItems: 'center', width: layout.width <= 768 ? '100%' : 'auto' }}>
                                {detail.daysLeft !== null && (
                                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                    <View>
                                      <Label style={{ fontSize: 12, color: theme.text.primary, marginTop: -4 }}>
                                        {detail.status === MissionAssignationStatus.ValidationRejected ? t('Validation-failed') : t('Mission-expires-the')}
                                      </Label>
                                      {!!detail.endDate && (
                                        <Label variant={LabelType.titleSmall} style={{ fontWeight: '500', flexShrink: 1 }} numberOfLines={2}>
                                          {detail.endDate.toLocaleString()}
                                        </Label>
                                      )}
                                    </View>
                                    <MoreInfoButton
                                      style={{ marginLeft: 8 }}
                                      title={''}
                                      color={theme.text.primary.toString()}
                                      size={16}
                                      openURL={'complete-the-questionnaire-lanla-website-support-link'}
                                    >
                                      {`${t('help.complete-the-questionnaire')}`}
                                    </MoreInfoButton>
                                  </View>
                                )}
                                {detail.isExtendable && (
                                  <Pressable
                                    style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: -8 }}
                                    onPress={() => handleDelayMissionClick(selectedMissionId, detail.maxExtendableDays)}
                                  >
                                    <FontAwesomeIcon icon={faCalendarClock} size={16} color={postponeInlineColor} />
                                    <Label
                                      variant={LabelType.title}
                                      style={[
                                        styles.newVersion.details.content.delayMission.text,
                                        { marginLeft: 4, color: postponeInlineColor, textDecorationLine: 'none' },
                                      ]}
                                      numberOfLines={3}
                                    >
                                      {t('Postpone-mission')}
                                    </Label>
                                  </Pressable>
                                )}
                                <View
                                  style={[
                                    { flexDirection: 'row' },
                                    useSinglePaneLayout && { width: '100%', paddingHorizontal: 15 },
                                  ]}
                                >
                                  <CustomButton
                                    title={
                                      detail.status === MissionAssignationStatus.ValidationRejected ||
                                      detail.status === MissionAssignationStatus.PrevalidationFailed
                                        ? t('Correct-my-questionnaire')
                                        : t('Complete-the-questionnaire')
                                    }
                                    disabled={!canAnswerCurrentMissionSurvey}
                                    onPress={showPrompt}
                                    style={useSinglePaneLayout ? styles.newVersion.details.header.checkIn.button : undefined}
                                  />
                                </View>
                                {Platform.OS === 'web' && !canAnswerCurrentMissionSurvey && (
                                  <Label style={surveyUnavailableMessageStyle} numberOfLines={3}>
                                    {getSurveyUnavailableMessage()}
                                  </Label>
                                )}
                              </View>
                            )}
                          </View>
                        </View>
                        {detail && (
                          <View style={styles.newVersion.details.content.container}>
                            <View style={styles.newVersion.details.content.leftContainer}>
                              {detail.supportedCultureCodes?.length > 0 && (
                                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
                                  <Label
                                    style={{
                                      fontSize: 12,
                                      color: isDarkMode ? '#ff9bb8' : theme.text.error?.toString(),
                                      fontWeight: '600',
                                    }}
                                  >
                                    {t('Mission-in')} {detail.supportedCultureCodes?.map((x) => t(x)).join(', ')} {t('only')}!
                                  </Label>
                                  <MoreInfoButton style={{ marginLeft: 8 }} title={''} color={theme.text.primary.toString()} size={12}>
                                    {t('help.mission-in-language-only', { cultures: detail.supportedCultureCodes?.map((x) => t(x)).join(', ') })}
                                  </MoreInfoButton>
                                </View>
                              )}
                              {(detail.status !== MissionAssignationStatus.PrevalidationFailed || !useSinglePaneLayout) && <>{getTimeline(false)}</>}
                            </View>
                            {getDetailComponent()}
                            {detail.status === MissionAssignationStatus.PrevalidationFailed && useSinglePaneLayout && <>{getTimeline(true)}</>}
                            <View style={{ ...styles.newVersion.details.content.actionButtonsContainer, marginBottom: 48, marginTop: -16 }}>
                              {useSinglePaneLayout &&
                                (detail.status < MissionAssignationStatus.Prevalidation || detail.status == MissionAssignationStatus.ValidationRejected) && (
                                  <Pressable
                                    style={styles.newVersion.details.content.cancelMission.container}
                                    onPress={() => {
                                      showConfirmationPrompt();
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faCircleXmark} size={Platform.OS === 'web' ? 32 : 24} color={'rgb(138, 47, 72)'} />
                                    <Label variant={LabelType.title} style={styles.newVersion.details.content.cancelMission.text}>
                                      {t('Cancel-mission')}
                                    </Label>
                                    <MoreInfoButton
                                      style={{ marginLeft: 8 }}
                                      title={''}
                                      color={'rgb(138, 47, 72)'}
                                      openURL={'cancel-the-mission-lanla-website-support-link'}
                                    >
                                      {`${t('help.cancel-the-mission')}`}
                                    </MoreInfoButton>
                                  </Pressable>
                                )}
                            </View>
                          </View>
                        )}
                      </ScrollView>
                    ) : (
                      <View style={{ flexGrow: 1 }}>
                        <Loading style={{ height: 200 }} />
                      </View>
                    )}
                  </View>
                )}
              </>
            )}
            {isWaitingForMissionListBootstrap && (!missionBlocks || missionBlocks.length === 0) && (
              <View style={{ flexGrow: 1 }}>
                <Loading style={{ height: 200 }} text={tMission('loading_missions')} />
              </View>
            )}
            {shouldShowEmptyMissionState && getEmptyComponent()}
          </View>
        </>
      )}
    </AuthGuard>
  );
};

export default MyMissions;
